'use client';

import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import {
  NOTIFICATIONS_REFRESH_EVENT,
  PortalLink,
  useCurrentUser,
  useNotificationsSource,
  useBlockPart,
  type NotificationItem,
} from '@akademi/site-builder-runtime';
import { Button, Card } from '@akademi/ui';
import { SectionShell } from '../_shared/SectionShell';
import type { PaddingBox } from '../_shared/resolvePadding';
import { EmptyState } from '../_shared/EmptyState';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { cardStyle } from '../_shared/styles';

export interface NotificationsTimelineProps {
  title?: string;
  emptyTitle?: string;
  emptyHint?: string;
  /** Page size per fetch — also the initial page size. This block
   *  reads the same {@code NotificationsSource} the header bell
   *  uses, just rendered as a timeline instead of a popover, with a
   *  "Show more" button driving {@code fetchPage}'s cursor pagination. */
  count?: number;
  /** Label for the pagination button shown when more pages remain. */
  showMoreLabel?: string;
  /** Design token size key e.g. "xl", "2xl". Maps to var(--font-size-{key}). */
  titleFontSize?: string;
  /** 'theme' (default) caps the block at the theme's content max-width; 'full' spans edge-to-edge. */
  maxWidth?: 'theme' | 'full';
  style?: CSSProperties;
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

type LoadState = 'loading' | 'error' | 'ready';

export function NotificationsTimeline({
  title: authoredTitle,
  emptyTitle: authoredEmptyTitle,
  emptyHint,
  count = 10,
  showMoreLabel: authoredShowMoreLabel,
  titleFontSize,
  maxWidth = 'theme',
  style,
  paddingOuter,
  paddingInner,
}: NotificationsTimelineProps) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const title = authoredTitle ?? t('notificationsTimeline.title');
  const emptyTitle = authoredEmptyTitle ?? t('notificationsTimeline.empty.title');
  const showMoreLabel = authoredShowMoreLabel ?? t('shared.showMore');
  const user = useCurrentUser();
  const source = useNotificationsSource();

  const [items, setItems] = useState<NotificationItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchFirstPage = useCallback(
    (signal?: AbortSignal) => {
      if (!source) return;
      setLoadState((cur) => (cur === 'ready' ? cur : 'loading'));
      source
        .fetchPage({ limit: count, signal })
        .then((page) => {
          if (signal?.aborted) return;
          setItems(Array.isArray(page.items) ? page.items : []);
          setNextCursor(page.nextCursor ?? null);
          setLoadState('ready');
        })
        .catch((err: unknown) => {
          if (signal?.aborted || (err instanceof DOMException && err.name === 'AbortError')) return;
          setLoadState('error');
        });
    },
    [source, count],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    fetchFirstPage(ctrl.signal);
    return () => ctrl.abort();
  }, [fetchFirstPage]);

  // A push message on the host's event bus means a notification was just
  // created server-side — re-pull page 1 so this timeline reflects it live
  // instead of only on next full page load.
  useEffect(() => {
    if (!source) return;
    const onRefresh = () => fetchFirstPage();
    window.addEventListener(NOTIFICATIONS_REFRESH_EVENT, onRefresh);
    return () => {
      window.removeEventListener(NOTIFICATIONS_REFRESH_EVENT, onRefresh);
    };
  }, [source, fetchFirstPage]);

  async function loadMore() {
    if (!source || !nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await source.fetchPage({ cursor: nextCursor, limit: count });
      const more = Array.isArray(page.items) ? page.items : [];
      setItems((cur) => [...cur, ...more]);
      setNextCursor(page.nextCursor ?? null);
    } finally {
      setLoadingMore(false);
    }
  }

  if (!user) return null;

  if (!source) {
    return (
      <SectionShell
        style={style}
        title={title}
        titleFontSize={titleFontSize}
        maxWidth={maxWidth}
        paddingOuter={paddingOuter}
        paddingInner={paddingInner}
      >
        <AdapterPlaceholder blockType="NotificationsTimeline" adapterName="NotificationsSource" />
      </SectionShell>
    );
  }

  if (loadState === 'loading') {
    return (
      <SectionShell
        style={style}
        title={title}
        titleFontSize={titleFontSize}
        maxWidth={maxWidth}
        paddingOuter={paddingOuter}
        paddingInner={paddingInner}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {[1, 2, 3, 4].map((i) => (
            <TimelineRowSkeleton key={i} />
          ))}
        </div>
      </SectionShell>
    );
  }

  if (loadState === 'error' || items.length === 0) {
    return (
      <SectionShell
        style={style}
        title={title}
        titleFontSize={titleFontSize}
        maxWidth={maxWidth}
        paddingOuter={paddingOuter}
        paddingInner={paddingInner}
      >
        <EmptyState title={emptyTitle} hint={emptyHint} />
      </SectionShell>
    );
  }

  return (
    <SectionShell
      style={style}
      title={title}
      titleFontSize={titleFontSize}
      maxWidth={maxWidth}
      paddingOuter={paddingOuter}
      paddingInner={paddingInner}
    >
      <Card
        {...part('list')}
        style={{
          ...cardStyle,
          padding: 'var(--space-4, 16px)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {items.map((item, idx) => (
          <TimelineRow key={item.id} item={item} isLast={idx === items.length - 1 && !nextCursor} />
        ))}
      </Card>
      {nextCursor && (
        <div
          style={{ display: 'flex', justifyContent: 'center', marginTop: 'var(--space-2, 8px)' }}
        >
          <Button
            variant="default"
            size="lg"
            onClick={loadMore}
            disabled={loadingMore}
            {...part('pager')}
          >
            {showMoreLabel}
          </Button>
        </div>
      )}
    </SectionShell>
  );
}

function TimelineRow({ item, isLast }: { item: NotificationItem; isLast: boolean }) {
  const inner = (
    <div style={{ display: 'flex', gap: 'var(--space-4, 16px)' }}>
      {/* Rail: dot + connecting line */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: 12,
          flexShrink: 0,
        }}
      >
        <span
          aria-hidden
          style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            marginTop: 5,
            flexShrink: 0,
            background: item.read
              ? 'var(--color-border-default, #d4d4d8)'
              : 'var(--color-brand-500, #d6007f)',
            border: '2px solid var(--color-surface-card, #fff)',
            boxShadow: '0 0 0 1px var(--color-border-subtle, #e4e4e7)',
          }}
        />
        {!isLast && (
          <span
            aria-hidden
            style={{ flex: 1, width: 2, background: 'var(--color-border-subtle, #e4e4e7)' }}
          />
        )}
      </div>

      {/* Content */}
      <div style={{ minWidth: 0, paddingBottom: 'var(--space-6, 24px)' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
          }}
        >
          {item.iconUrl && (
            <img
              src={item.iconUrl}
              alt=""
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                objectFit: 'cover',
                flexShrink: 0,
              }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          )}
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: '14px',
                fontWeight: item.read ? 400 : 600,
                lineHeight: 1.5,
                color: 'var(--color-text-primary, #18181b)',
              }}
            >
              {item.label ? <item.label /> : `${item.title}${item.body ? ` — ${item.body}` : ''}`}
            </div>
            <div
              style={{
                marginTop: 4,
                fontSize: '12px',
                color: 'var(--color-text-tertiary, #71717a)',
              }}
            >
              {formatTimelineDate(item.createdAt)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return item.href ? (
    <PortalLink href={item.href} style={{ color: 'inherit', textDecoration: 'none' }}>
      {inner}
    </PortalLink>
  ) : (
    inner
  );
}

function TimelineRowSkeleton() {
  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--space-4, 16px)',
        paddingBottom: 'var(--space-6, 24px)',
      }}
    >
      <div
        style={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          marginTop: 5,
          background: 'var(--color-neutral-100, #f4f4f5)',
          animation: 'pulse 1.5s ease-in-out infinite',
        }}
      />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div
          style={{
            height: 13,
            width: '70%',
            borderRadius: 4,
            background: 'var(--color-neutral-100, #f4f4f5)',
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
        <div
          style={{
            height: 10,
            width: '30%',
            borderRadius: 4,
            background: 'var(--color-neutral-100, #f4f4f5)',
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
      </div>
    </div>
  );
}

function formatTimelineDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
